import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiClipboard, FiSliders, FiCopy, FiArrowRight } from 'react-icons/fi';
import Footer from '../components/Layout/Footer';

const STEPS = [
  { icon: <FiClipboard size={32} />, title: 'Paste Your Message or Draft', desc: 'Paste the email you received into ReplyCraft — or switch to "Fix My Draft" mode and paste your rough draft to polish it. Works with any format: formal emails, chat messages, LinkedIn messages, customer complaints, and more.' },
  { icon: <FiSliders size={32} />, title: 'Customize Everything', desc: 'Select tones (Professional, Warm, Assertive, Casual, Diplomatic), choose reply length (Short, Medium, Long), pick a language from 24+ options, set context category (Job, Client, Sales, Support, etc.), and add reply instructions or click smart suggestion chips to tell the AI exactly what to say.' },
  { icon: <FiCopy size={32} />, title: 'Copy, Email, or Save', desc: 'Get multiple reply options instantly. Copy your favorite, open it directly in Gmail/Outlook with the Email button, or save it with the heart icon for later. Your custom signature (name, title, company) is auto-applied.' }
];

export default function HowItWorks() {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <Link to="/" style={styles.brand}>ReplyCraft</Link>
        <button onClick={() => navigate('/')} style={styles.tryBtn}>Try It Free</button>
      </nav>

      <div style={styles.container}>
        <h1 style={styles.title}>How ReplyCraft Works</h1>
        <p style={styles.subtitle}>Three simple steps to the perfect reply</p>

        <div style={styles.steps}>
          {STEPS.map((step, i) => (
            <div key={i} style={styles.stepCard}>
              <div style={styles.stepNumber}>{i + 1}</div>
              <div style={styles.stepIcon}>{step.icon}</div>
              <h3 style={styles.stepTitle}>{step.title}</h3>
              <p style={styles.stepDesc}>{step.desc}</p>
            </div>
          ))}
        </div>

        <div style={styles.example}>
          <h2 style={styles.exampleTitle}>Example: Generate Reply</h2>
          <div style={styles.exampleGrid}>
            <div style={styles.exampleBox}>
              <h4 style={styles.exampleLabel}>You receive:</h4>
              <p style={styles.exampleText}>"Hi, I wanted to follow up on the proposal I sent last week. Could you let me know your thoughts?"</p>
              <h4 style={{ ...styles.exampleLabel, marginTop: 12 }}>Your instructions:</h4>
              <p style={styles.exampleText}>"Thank them, say I reviewed it, and schedule a call for next week"</p>
            </div>
            <div style={styles.arrow}><FiArrowRight size={24} /></div>
            <div style={styles.exampleBox}>
              <h4 style={styles.exampleLabel}>ReplyCraft generates:</h4>
              <div style={styles.toneExample}>
                <span style={{ ...styles.toneBadge, background: '#3b82f622', color: '#3b82f6' }}>Professional</span>
                <p style={styles.exampleText}>"Thank you for following up. I've reviewed the proposal thoroughly and would like to discuss a few points. Could we schedule a call next week at your convenience?"</p>
              </div>
              <div style={styles.toneExample}>
                <span style={{ ...styles.toneBadge, background: '#f59e0b22', color: '#f59e0b' }}>Warm</span>
                <p style={styles.exampleText}>"Thanks so much for checking in! I've gone through the proposal and I'm really excited about it. Let's set up a call next week to chat more about the details!"</p>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.example}>
          <h2 style={styles.exampleTitle}>Example: Fix My Draft</h2>
          <div style={styles.exampleGrid}>
            <div style={styles.exampleBox}>
              <h4 style={styles.exampleLabel}>Your rough draft:</h4>
              <p style={styles.exampleText}>"hey thanks for the proposal. looks good. lets talk next week maybe tuesday or wednesday"</p>
            </div>
            <div style={styles.arrow}><FiArrowRight size={24} /></div>
            <div style={styles.exampleBox}>
              <h4 style={styles.exampleLabel}>ReplyCraft polishes it:</h4>
              <div style={styles.toneExample}>
                <span style={{ ...styles.toneBadge, background: '#3b82f622', color: '#3b82f6' }}>Professional</span>
                <p style={styles.exampleText}>"Thank you for sending over the proposal. I've reviewed it and it looks promising. Would you be available for a call next Tuesday or Wednesday to discuss the details further?"</p>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.cta}>
          <h2>Ready to try it?</h2>
          <p style={{ color: 'var(--text-secondary)', margin: '8px 0 20px' }}>Start with 1,000 free credits. No credit card required.</p>
          <button onClick={() => navigate('/')} style={styles.ctaBtn}>Get Started Free <FiArrowRight style={{ marginLeft: 8 }} /></button>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column' },
  nav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' },
  brand: { fontSize: 20, fontWeight: 700, color: 'var(--accent)', textDecoration: 'none' },
  tryBtn: { padding: '8px 20px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  container: { maxWidth: 800, margin: '0 auto', padding: '48px 20px 60px', flex: 1 },
  title: { fontSize: 'clamp(26px, 6vw, 40px)', fontWeight: 800, textAlign: 'center', marginBottom: 8, color: 'var(--text-primary)' },
  subtitle: { textAlign: 'center', color: 'var(--text-secondary)', fontSize: 18, marginBottom: 48 },
  steps: { display: 'flex', flexDirection: 'column', gap: 24 },
  stepCard: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, padding: 32, position: 'relative' },
  stepNumber: { position: 'absolute', top: -16, left: 32, width: 36, height: 36, borderRadius: '50%', background: 'var(--accent)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18 },
  stepIcon: { color: 'var(--accent)', marginBottom: 12 },
  stepTitle: { fontSize: 22, fontWeight: 700, marginBottom: 8, color: 'var(--text-primary)' },
  stepDesc: { fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.7 },
  example: { marginTop: 56, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, padding: 32 },
  exampleTitle: { fontSize: 22, fontWeight: 700, marginBottom: 20, color: 'var(--text-primary)' },
  exampleGrid: { display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' },
  exampleBox: { flex: 1, minWidth: 240 },
  exampleLabel: { fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 },
  exampleText: { fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, fontStyle: 'italic' },
  arrow: { color: 'var(--accent)', paddingTop: 20, display: 'flex', alignItems: 'center' },
  toneExample: { marginBottom: 12 },
  toneBadge: { display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, marginBottom: 6 },
  cta: { textAlign: 'center', marginTop: 56, padding: 40, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16 },
  ctaBtn: { display: 'inline-flex', alignItems: 'center', padding: '14px 32px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer' }
};
